<template>
    <div class="key-points">
        <ul v-if="enabled" class="key-points__list">
            <li v-if="hasNumber" class="key-points__item key-points__item--number">
                <div class="badge"><NuxtIcon name="ri:phone-fill" /></div>
                <span>{{ airport?.content.phone }}</span>
            </li>
            <li v-if="hasPostcode" class="key-points__item key-points__item--postcode">
                <div class="badge"><NuxtIcon name="ri:map-pin-fill" /></div>
                <span>{{ airport?.content.address[0].postal_code }}</span>
            </li>
            <li v-if="hasTerminals" class="key-points__item key-points__item--terminals">
                <div class="badge"><NuxtIcon name="ri:plane-fill" class="rotate-45" /></div>
                <span v-for="(terminal, index) in airportContent?.terminals" :key="index">
                    {{ terminalFormat(terminal.name) }}
                    {{ index !== airportContent?.terminals!.length - 1 ? `, ` : '' }}
                </span>
            </li>
            <li v-if="hasTransport" class="key-points__item key-points__item--options">
                <div class="badge"><NuxtIcon name="ri:bus-fill" /></div>
                <span>{{ airportContent?.travel_options }}</span>
            </li>
            <li v-if="hasFreeWifi" class="key-points__item key-points__item--wifi">
                <div class="badge"><NuxtIcon name="ri:wifi-line" /></div>
                <span>{{ airportContent?.wifi }}</span>
            </li>
            <li v-if="hasWebsite" class="key-points__item key-points__item--website">
                <div class="badge"><NuxtIcon name="ri:computer-line" /></div>
                <span>
                    <a :href="airport?.content.link.url" target="_blank" rel="noopener">
                        {{ clearUrl(airport?.content.link.url) }}
                    </a>
                </span>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { clearUrl } from '~/utils/helpers';
import type { IL4Airport, IL4AirportContent } from '~/types/components/L4Airport';

const { airport, airportContent, enabled } = defineProps<{
    airport: IL4Airport;
    airportContent: IL4AirportContent;
    enabled: boolean;
}>();

const { t } = useI18n();

const hasNumber: ComputedRef<boolean> = computed(() => airport?.content.phone && airport?.content.phone.length > 0);
const hasPostcode: ComputedRef<boolean> = computed(
    () => airport?.content.address.length > 0 && airport?.content.address[0].postal_code.length > 0
);
const hasTerminals: ComputedRef<boolean> = computed(
    () => airportContent?.terminals && airportContent.terminals.length > 0
);
const hasTransport: ComputedRef<boolean> = computed(
    () => airportContent?.travel_options && airportContent?.travel_options.length > 0
);
const hasFreeWifi: ComputedRef<boolean> = computed(() => airportContent?.wifi && airportContent.wifi.length > 0);
const hasWebsite: ComputedRef<boolean> = computed(() => airport?.content.link && airport?.content.link.url.length > 0);

const terminalFormat = (terminal: string) => {
    if (hasTerminals.value && airportContent?.terminals && airportContent.terminals.length < 2)
        return t('tabs.one_terminal');
    return terminal.length === 1 ? `T${terminal}` : `${terminal} ${t('tabs.terminal')}`;
};
</script>

<style lang="postcss" scoped>
.key-points {
    @apply md:float-right p-2 pl-1 md:ml-2 md:w-[260px] bg-[#efefef];
    border: 1px solid #c5c5c5;
    &__list {
        @apply list-none pl-0 !important;
    }
    &__item {
        @apply relative pl-2 mb-1 text-sm leading-6 flex items-center;
        .badge {
            @apply bg-gray-800 rounded-full w-5 h-5 mr-2 flex items-center justify-center;
            .iconify {
                @apply bg-white;
            }
        }
        a {
            @apply underline;
        }
    }
}
</style>
